/**
 * 플레어 헤더 계산서.
 *
 * 근거: API Standard 521, 2020년 6월판 5.5
 *   라인 사이징 5.5.3 · 다중 분출 5.5.4 · 등온 압력강하 5.5.5 · 부속 5.5.9
 *
 * 검토자가 확인해야 하는 것은 세 가지다.
 *   ① 어떤 조합을 봤는가 — 빠뜨린 조합이 없는가
 *   ② 밸브마다 어느 조합이 지배하는가
 *   ③ 지배 조합에서 헤더가 어떻게 풀렸는가
 *
 * 그래서 조합 비교표를 먼저 놓고, 조합별 상세를 뒤에 붙인다.
 */
import { gaugeLabel, type PressureUnit } from "./pressureUnit";
import { MATERIAL_LABELS } from "./pipingState";
import {
  compareScenarios,
  runFlare,
  scenarioFlow,
  type FlarePipeSpec,
  type FlareState,
} from "./flareState";

const nf = (v: number, d = 1) =>
  Number.isFinite(v)
    ? v.toLocaleString("ko-KR", {
        minimumFractionDigits: d,
        maximumFractionDigits: d,
      })
    : "—";

/** 관경·길이를 한 줄로 요약한다 — 표에 넣기 위해서다. */
function pipeSummary(p: FlarePipeSpec): string {
  const size = p.useCustomId
    ? `내경 ${p.customIdMm} mm`
    : `${p.nps}" Sch ${p.schedule}`;
  const allowance =
    p.fittingAllowancePercent.trim() === ""
      ? ""
      : ` + 여유 ${p.fittingAllowancePercent} %`;
  return `${size} · 직관 ${p.straightLengthM} m${allowance}`;
}

export function FlareSheet({
  value,
  unit,
  today,
}: {
  value: FlareState;
  unit: PressureUnit;
  today: string;
}) {
  const comparison = compareScenarios(value, unit);
  const labelOf = (id: string | null) =>
    id === null
      ? "플레어 팁"
      : (value.segments.find((s) => s.id === id)?.label ?? "?");

  return (
    <section className="card sheet">
      <header className="sheet__head">
        <div>
          <h2 className="sheet__title">플레어 헤더 배압 계산서</h2>
          <p className="sheet__sub">API 521 (2020) 5.5 · 등온 압축성 유동</p>
        </div>
        <div className="sheet__meta">
          <div>
            <dt>조합 수</dt>
            <dd className="sheet__tag">{value.scenarios.length}</dd>
          </div>
          <div>
            <dt>작성일</dt>
            <dd>{today}</dd>
          </div>
        </div>
      </header>

      <h3 className="sheet__h3">1. 검토 조건</h3>
      <table className="sheet__table">
        <tbody>
          <tr>
            <th>플레어 팁 압력</th>
            <td>
              {value.tipPressure} {gaugeLabel(unit)}
            </td>
            <th>마하수 상한</th>
            <td>
              {value.machLimit}
              <span className="muted"> (설계자 지정, 5.5.3)</span>
            </td>
          </tr>
          <tr>
            <th>계산 방법</th>
            <td colSpan={3}>
              등온 압축성 유동 — API 521 식 (33)·(34)·(36). 플레어 팁에서
              상류로 거슬러 올라가며 관경이 일정한 구간마다 계산 (5.5.5).
            </td>
          </tr>
        </tbody>
      </table>

      <h3 className="sheet__h3">2. 헤더 구성</h3>
      <table className="sheet__table sheet__table--grid">
        <thead>
          <tr>
            <th>구간</th>
            <th>흘러가는 곳</th>
            <th>배관 사양</th>
            <th>재질</th>
          </tr>
        </thead>
        <tbody>
          {value.segments.map((s) => (
            <tr key={s.id}>
              <td>{s.label}</td>
              <td>{labelOf(s.downstreamId)}</td>
              <td>{pipeSummary(s.pipe)}</td>
              <td className="muted">{MATERIAL_LABELS[s.pipe.material]}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h4 className="sheet__h4">밸브 및 테일파이프</h4>
      <table className="sheet__table sheet__table--grid">
        <thead>
          <tr>
            <th>태그</th>
            <th>합류 구간</th>
            <th>분자량</th>
            <th>배출 온도</th>
            <th>Z</th>
            <th>점도</th>
            <th>테일파이프</th>
          </tr>
        </thead>
        <tbody>
          {value.valves.map((v) => (
            <tr key={v.id}>
              <td>{v.tag}</td>
              <td>{labelOf(v.segmentId)}</td>
              <td className="cell">{v.molecularWeight}</td>
              <td className="cell">{v.temperatureK} K</td>
              <td className="cell">{v.compressibility}</td>
              <td className="cell">{v.viscosityCp} cP</td>
              <td>{pipeSummary(v.tail)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h3 className="sheet__h3">3. 동시 분출 조합 비교</h3>
      <p className="sheet__note">
        헤더 관경은 <b>가장 나쁜 조합</b>으로 정합니다. 총 유량이 큰 조합이
        반드시 지배하는 것은 아닙니다 — 유량이 한 지선에 몰리면 그쪽 밸브의
        배압이 더 높아집니다 (5.5.2, 5.5.4).
      </p>
      <div className="table-scroll">
        <table className="sheet__table sheet__table--grid">
          <thead>
            <tr>
              <th>밸브</th>
              {value.scenarios.map((sc) => (
                <th key={sc.id}>{sc.name}</th>
              ))}
              <th>최악 배압</th>
              <th>지배 조합</th>
            </tr>
          </thead>
          <tbody>
            {value.valves.map((v) => {
              const worst = comparison.worstPerValve.find(
                (w) => w.valveId === v.id,
              );
              return (
                <tr key={v.id}>
                  <td>{v.tag}</td>
                  {value.scenarios.map((sc) => {
                    const sum = comparison.summaries.find(
                      (x) => x.scenarioId === sc.id,
                    );
                    const bp = sum?.backpressures.get(v.id);
                    return (
                      <td key={sc.id} className="cell">
                        {scenarioFlow(sc, v.id)} kg/h
                        {bp !== undefined && (
                          <>
                            <br />
                            <span className="muted">{nf(bp, 1)} kPag</span>
                          </>
                        )}
                      </td>
                    );
                  })}
                  <td className="cell">
                    <b>
                      {worst ? `${nf(worst.backpressureKpaGauge, 1)} kPag` : "—"}
                    </b>
                  </td>
                  <td className="muted">{worst?.scenarioName ?? "—"}</td>
                </tr>
              );
            })}
            <tr>
              <td className="muted">총 유량</td>
              {comparison.summaries.map((sum) => (
                <td key={sum.scenarioId} className="cell muted">
                  {sum.ok ? `${nf(sum.totalFlowKgH, 0)} kg/h` : "계산 불가"}
                </td>
              ))}
              <td colSpan={2} />
            </tr>
            <tr>
              <td className="muted">헤더 최대 Ma</td>
              {comparison.summaries.map((sum) => (
                <td key={sum.scenarioId} className="cell muted">
                  {sum.ok ? nf(sum.maxHeaderMach, 3) : "—"}
                  {sum.machExceeded && " (상한 초과)"}
                </td>
              ))}
              <td colSpan={2} />
            </tr>
          </tbody>
        </table>
      </div>

      <h3 className="sheet__h3">4. 조합별 계산 상세</h3>
      {value.scenarios.map((sc, i) => {
        const { result } = runFlare(value, unit, sc.id);
        return (
          <div className="sheet__case" key={sc.id}>
            <h4 className="sheet__h4">
              4.{i + 1} {sc.name}
            </h4>

            {!result.ok ? (
              <p className="sheet__note sheet__note--warn">
                계산 불가 — {result.message}
              </p>
            ) : (
              <>
                <table className="sheet__table sheet__table--grid">
                  <thead>
                    <tr>
                      <th>구간</th>
                      <th>누적 유량</th>
                      <th>내경</th>
                      <th>입구 → 출구</th>
                      <th>강하</th>
                      <th>출구 Ma</th>
                      <th>임계압</th>
                      <th>Re</th>
                      <th>f</th>
                      <th>혼합 M · T</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.segments.map((s) => (
                      <tr key={s.id}>
                        <td>
                          {"　".repeat(s.depth)}
                          {s.label}
                        </td>
                        <td className="cell">{nf(s.massFlowKgH, 0)} kg/h</td>
                        <td className="cell">
                          {nf(s.innerDiameterM * 1000, 1)} mm
                        </td>
                        <td className="cell">
                          {nf(s.inletPressureKpaAbs, 1)} →{" "}
                          {nf(s.outletPressureKpaAbs, 1)}
                        </td>
                        <td className="cell">{nf(s.pressureDropKpa, 1)}</td>
                        <td className="cell">{nf(s.outletMach, 3)}</td>
                        <td className="cell">
                          {nf(s.criticalPressureKpaAbs, 1)}
                        </td>
                        <td className="cell">{s.reynolds.toExponential(2)}</td>
                        <td className="cell">{s.frictionFactor.toFixed(4)}</td>
                        <td className="cell">
                          {nf(s.molecularWeight, 1)} ·{" "}
                          {nf(s.temperatureK, 0)} K
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="sheet__note">압력 단위 kPa abs · 강하 kPa</p>

                <table className="sheet__table sheet__table--grid">
                  <thead>
                    <tr>
                      <th>밸브</th>
                      <th>유량</th>
                      <th>합류점 압력</th>
                      <th>테일 강하</th>
                      <th>총 배압</th>
                      <th>테일 Ma</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.valves.map((v) => (
                      <tr key={v.id}>
                        <td>{v.tag}</td>
                        <td className="cell">{nf(v.massFlowKgH, 0)} kg/h</td>
                        <td className="cell">
                          {nf(v.nodePressureKpaAbs, 1)} kPa abs
                        </td>
                        <td className="cell">{nf(v.tailDropKpa, 1)} kPa</td>
                        <td className="cell">
                          <b>{nf(v.backpressureKpaGauge, 1)} kPag</b>
                        </td>
                        <td className="cell">{nf(v.tailOutletMach, 3)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {result.warnings.length > 0 && (
                  <ul className="sheet__warnings">
                    {result.warnings.map((w) => (
                      <li key={w}>{w}</li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
        );
      })}

      <footer className="sheet__foot">
        <p>
          근거: API Standard 521, June 2020, 5.5 · KOSHA GUIDE D-63-2018
        </p>
        <p className="muted">
          등온 압축성 유동식으로 계산했습니다 — API 521 식 (33)·(34)·(36) 과
          KOSHA GUIDE D-63-2018 식 (5-7)·(5-12)·(5-15) 는 같은 식입니다.
          마찰계수는 KOSHA 절차대로 레이놀즈수(식 5-9)를 구한 뒤
          Colebrook–White(식 5-10)로 정했고, 합류지점 점도는 식 (5-4)로
          섞었습니다.
          두 기준 모두 단열식보다 <b>등온식을 권장</b>합니다 (API 521 5.5.5,
          KOSHA D-63 4.1(1)). 연결지점 물성은 KOSHA 식 (5-2)·(5-3) 을
          따랐습니다. 마하수는 등온 음속 기준이며 초크 한계가 1.0 입니다.
          KOSHA 는 0.8 미만, 가능한 한 0.6 이하를 권합니다 (5.2.6.2).
          혼합 온도·압축계수는 가중평균 근사이며, 온도 차가 큰 조합은 엔탈피
          수지로 다시 확인해야 합니다. 부속 등가길이는 KOSHA &lt;표1&gt;,
          배관 치수는 ASME B36.10M 상용 치수입니다.
        </p>
      </footer>
    </section>
  );
}
